import { Role } from 'core/models/role.model';

/** Visual tokens per role - sidebar + global accent. */
export interface RoleTheme {
  readonly sidebarBg: string;
  readonly sidebarBgHover: string;
  readonly sidebarBorder: string;
}

export const ROLE_THEME: Record<Role, RoleTheme> = {
  TEACHER: {
    sidebarBg: '#392a74',
    sidebarBgHover: '#5A4B82',
    sidebarBorder: '#584A7A',
  },
  STUDENT: {
    sidebarBg: '#0b4f9a',
    sidebarBgHover: '#2F86C9',
    sidebarBorder: '#2F7FBF',
  },
  GUARDIAN: {
    sidebarBg: '#13753d',
    sidebarBgHover: '#26855A',
    sidebarBorder: '#268556',
  },
  SECRETARY: {
    sidebarBg: '#E67E22',
    sidebarBgHover: '#CF6E18',
    sidebarBorder: '#C96C18',
  },
  INSTITUTION_ADMIN: {
    sidebarBg: '#3e489b',
    sidebarBgHover: '#263D73',
    sidebarBorder: '#263D72',
  },
  SUPER_ADMIN: {
    sidebarBg: '#2C3E50',
    sidebarBgHover: '#233242',
    sidebarBorder: '#1F2D3A',
  },
  PLATFORM_ADMIN: {
    sidebarBg: '#34495E',
    sidebarBgHover: '#2C3E50',
    sidebarBorder: '#2A3A4A',
  },
  SUPPORT: {
    sidebarBg: '#5D6D7E',
    sidebarBgHover: '#4E5C6B',
    sidebarBorder: '#4A5866',
  },
  FINANCE: {
    sidebarBg: '#1A7A6D',
    sidebarBgHover: '#15675C',
    sidebarBorder: '#15675C',
  },
  TRANSPORT_OPERATOR: {
    sidebarBg: '#1E88A8',
    sidebarBgHover: '#197491',
    sidebarBorder: '#197391',
  },
};

export const DEFAULT_ROLE_THEME: RoleTheme = ROLE_THEME.SUPER_ADMIN;

export function getRoleTheme(role: Role | null | undefined): RoleTheme {
  if (!role) {
    return DEFAULT_ROLE_THEME;
  }
  return ROLE_THEME[role] ?? DEFAULT_ROLE_THEME;
}

/** CSS custom properties for layout root - consumed by ui/components/pages. */
export function getRoleThemeCssVars(
  role: Role | null | undefined,
): Record<string, string> {
  const theme = getRoleTheme(role);
  const primary = theme.sidebarBg;
  return {
    '--color-primary': primary,
    '--color-primary-hover': theme.sidebarBgHover,
    '--color-primary-soft': `color-mix(in srgb, ${primary} 14%, white)`,
    '--color-primary-muted': `color-mix(in srgb, ${primary} 28%, white)`,
    '--color-primary-ring': `color-mix(in srgb, ${primary} 22%, white)`,
    '--color-on-primary': '#ffffff',
    '--sidebar-bg': theme.sidebarBg,
    '--sidebar-bg-hover': theme.sidebarBgHover,
    '--sidebar-border': theme.sidebarBorder,
  };
}
